import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import type {
  SleepDecision,
  TrafficCount,
  TrafficDay,
  TrafficDetail,
  TrafficOverview,
  TrafficOverviewEntry,
} from '../shared/traffic'
import { api } from './api'
import { relativeTime } from './format'

type TierFilter = 'all' | 'always-on' | 'sleep' | 'asleep'

const number = new Intl.NumberFormat('en-US')

function Sparkline({ days }: { days: TrafficDay[] }) {
  const max = Math.max(1, ...days.map((day) => day.views))
  return (
    <span className="traffic-spark" aria-label="Views per day, last 30 days">
      {days.map((day) => (
        <span
          key={day.day}
          style={{ height: `${Math.max(day.views ? 12 : 4, (day.views / max) * 100)}%` }}
          className={day.views ? '' : 'empty'}
          title={`${day.day}: ${number.format(day.views)} views, ${number.format(day.visitors)} visitors`}
        />
      ))}
    </span>
  )
}

function TierBadge({ entry }: { entry: TrafficOverviewEntry }) {
  const sleep = entry.sleep
  if (!sleep) return <span className="traffic-tier tier-none">Not managed</span>
  if (sleep.state === 'asleep') return <span className="traffic-tier tier-asleep">Asleep</span>
  if (sleep.state === 'waking') return <span className="traffic-tier tier-waking">Waking</span>
  if (sleep.tier === 'sleep' && !sleep.approved) return <span className="traffic-tier tier-candidate">Candidate</span>
  return sleep.tier === 'sleep' ? (
    <span className="traffic-tier tier-sleep">Sleeps when idle</span>
  ) : (
    <span className="traffic-tier tier-on">Always on</span>
  )
}

function CountList({ title, items, empty }: { title: string; items: TrafficCount[]; empty: string }) {
  return (
    <div className="traffic-list">
      <h4>{title}</h4>
      {items.length ? (
        <ol>
          {items.map((item) => (
            <li key={item.key}>
              <span title={item.key}>{item.key}</span>
              <strong>{number.format(item.count)}</strong>
            </li>
          ))}
        </ol>
      ) : (
        <p>{empty}</p>
      )}
    </div>
  )
}

function Detail({ entry }: { entry: TrafficOverviewEntry }) {
  const [detail, setDetail] = useState<TrafficDetail>()
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    api
      .trafficDetail(entry.applicationId)
      .then((value) => live && setDetail(value))
      .catch((reason: Error) => live && setError(reason.message))
    return () => {
      live = false
    }
  }, [entry.applicationId])

  if (entry.coverage === 'unobserved') {
    return <div className="traffic-detail"><p>No access-log records have been observed for this app. Usage is unknown.</p></div>
  }

  return (
    <div className="traffic-detail">
      {entry.sleep && (
        <div className="traffic-reasons">
          <strong>{entry.sleep.tier === 'sleep' ? 'Sleeps when idle because' : 'Stays on because'}</strong>
          <ul>
            {entry.sleep.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <small>
            Compose project <code>{entry.sleep.project}</code>
            {entry.sleep.lastActivityAt && <> · last activity {relativeTime(entry.sleep.lastActivityAt)}</>}
            {entry.oldestAt && <> · {number.format(entry.records)} records kept since {entry.oldestAt.slice(0, 10)}</>}
          </small>
        </div>
      )}
      {error && <p className="traffic-error">{error}</p>}
      {!detail && !error && <p className="traffic-muted">Reading the last seven days…</p>}
      {detail && (
        <>
          <div className="traffic-lists">
            <CountList title="Top pages (7 days)" items={detail.topPaths} empty="No views" />
            <CountList title="Referrers" items={detail.topReferrers} empty="No external referrers" />
            <CountList title="Visitors by IP" items={detail.topVisitors} empty="No human visitors" />
            <CountList title="Status codes" items={detail.statuses} empty="No requests" />
          </div>
          <div className="traffic-recent">
            <h4>Latest requests</h4>
            <div className="traffic-scroll">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>IP</th>
                    <th>Request</th>
                    <th>Status</th>
                    <th>Kind</th>
                    <th>Client</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.recent.map((record, index) => (
                    <tr key={`${record.t}-${index}`}>
                      <td>{new Date(record.t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                      <td className="mono">{record.ip}</td>
                      <td className="mono" title={`${record.h}${record.u}`}>
                        {record.m} {record.u}
                      </td>
                      <td>{record.s}</td>
                      <td>{record.v ? 'view' : record.c}</td>
                      <td className="traffic-agent" title={record.ua}>{record.ua}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export function TrafficPanel() {
  const [overview, setOverview] = useState<TrafficOverview>()
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<TierFilter>('all')
  const [open, setOpen] = useState<string>()
  const [pending, setPending] = useState<string>()

  const load = useCallback(async () => {
    try {
      setOverview(await api.traffic())
      setError('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Traffic could not be loaded')
    }
  }, [])

  useEffect(() => {
    void load()
    const timer = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(timer)
  }, [load])

  const rows = useMemo(() => {
    const entries = (overview?.applications ?? []).filter((entry) => entry.records > 0 || entry.sleep)
    return entries
      .filter((entry) => {
        if (filter === 'all') return true
        if (filter === 'asleep') return entry.sleep?.state === 'asleep'
        if (filter === 'sleep') return entry.sleep?.tier === 'sleep'
        return entry.sleep?.tier === filter
      })
      .sort((left, right) => Number(left.coverage === 'unobserved') - Number(right.coverage === 'unobserved') || right.visits7d - left.visits7d || left.name.localeCompare(right.name))
  }, [overview, filter])

  const counts = useMemo(() => {
    const managed = (overview?.applications ?? []).map((entry) => entry.sleep).filter(Boolean)
    const projects = new Map(managed.map((sleep) => [sleep!.project, sleep!]))
    return {
      on: [...projects.values()].filter((sleep) => sleep.tier === 'always-on').length,
      sleep: [...projects.values()].filter((sleep) => sleep.tier === 'sleep' && sleep.approved).length,
      candidates: [...projects.values()].filter((sleep) => sleep.tier === 'sleep' && !sleep.approved).length,
      asleep: [...projects.values()].filter((sleep) => sleep.state === 'asleep').length,
    }
  }, [overview])

  const candidates = useMemo(() => {
    const byProject = new Map<string, { sleep: SleepDecision; entries: TrafficOverviewEntry[] }>()
    for (const entry of overview?.applications ?? []) {
      if (entry.sleep?.tier !== 'sleep') continue
      const existing = byProject.get(entry.sleep.project)
      if (existing) existing.entries.push(entry)
      else byProject.set(entry.sleep.project, { sleep: entry.sleep, entries: [entry] })
    }
    return [...byProject.values()].sort((left, right) => left.sleep.visits7d - right.sleep.visits7d)
  }, [overview])

  async function approve(project: string, approved: boolean) {
    setPending(project)
    try {
      setOverview(await api.approveSleep(project, approved))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save the approval')
    } finally {
      setPending(undefined)
    }
  }

  const policy = overview?.policy
  return (
    <section className="traffic-section">
      <header className="traffic-head">
        <div>
          <span className="section-kicker">Access logs</span>
          <h2>Traffic and sleep</h2>
          {policy && overview && (
            <p>
              Estimated external visits exclude known team addresses, signed-in Relay browsers, marked tests, bots and checks.
              Sources with no records show “Not observed”. IP visitors are estimates; they are not authenticated daily active users.
              Always on at {number.format(policy.activeVisits)}+ visits in 7 days, a deploy in the last {policy.recentDays} days,
              or active work ({policy.activeCommits}+ commits on any branch in that time). Anything quieter is only a
              candidate until you approve it. Logs keep {overview.retention.days} days, or the newest{' '}
              {number.format(overview.retention.minimumRecords)} records if that reaches further back.
            </p>
          )}
        </div>
        <div className="traffic-totals">
          <span><strong>{counts.on}</strong> always on</span>
          <span><strong>{counts.candidates}</strong> awaiting approval</span>
          <span><strong>{counts.sleep}</strong> approved to sleep</span>
          <span><strong>{counts.asleep}</strong> asleep now</span>
          {overview?.ingestedAt && <small>Logs read {relativeTime(overview.ingestedAt)}</small>}
        </div>
      </header>

      {candidates.length > 0 && (
        <section className="sleep-candidates" aria-label="Sleep candidates">
          <h3>Sleep candidates</h3>
          <p className="traffic-muted">
            Nothing sleeps until it is approved here. An approved app stops after {policy?.idleMinutes} idle minutes and
            starts again on the next real visit; approval is withdrawn automatically if it gets busy or worked on.
          </p>
          <ul>
            {candidates.map(({ sleep, entries }) => (
              <li key={sleep.project}>
                <div>
                  <strong>{entries.map((entry) => entry.name).join(', ')}</strong>
                  <small>
                    {number.format(sleep.visits7d)} visits in 7 days
                    {entries[0]?.lastVisitAt && <> · last visit {relativeTime(entries[0].lastVisitAt)}</>}
                    {sleep.lastCommitAt && <> · last commit {relativeTime(sleep.lastCommitAt)}</>}
                    {sleep.state === 'asleep' && <> · asleep now</>}
                  </small>
                </div>
                <button
                  className={sleep.approved ? 'approved' : ''}
                  disabled={pending === sleep.project}
                  onClick={() => void approve(sleep.project, !sleep.approved)}
                >
                  {sleep.approved ? 'Approved · revoke' : 'Approve sleep'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="traffic-filters" role="tablist" aria-label="Filter by tier">
        {(
          [
            ['all', 'All'],
            ['always-on', 'Always on'],
            ['sleep', 'Candidates & approved'],
            ['asleep', 'Asleep now'],
          ] as Array<[TierFilter, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={filter === id}
            className={filter === id ? 'active' : ''}
            onClick={() => setFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="traffic-error">{error}</p>}
      {!overview && !error && <p className="traffic-muted">Loading traffic…</p>}

      {overview && (
        <div className="traffic-scroll">
          <table className="traffic-table">
            <thead>
              <tr>
                <th>App</th>
                <th>Tier</th>
                <th className="num">Visits 24h</th>
                <th className="num">Visits 7d</th>
                <th className="num">Page loads 7d</th>
                <th className="num">Visitors 7d</th>
                <th className="num">Bots 7d</th>
                <th>Last visit</th>
                <th>30 days</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => {
                const expanded = open === entry.applicationId
                return (
                  <Fragment key={entry.applicationId}>
                    <tr
                      className={expanded ? 'expanded' : ''}
                      onClick={() => setOpen(expanded ? undefined : entry.applicationId)}
                    >
                      <td>
                        <button
                          className="traffic-name"
                          aria-expanded={expanded}
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpen(expanded ? undefined : entry.applicationId)
                          }}
                        >
                          <strong>{entry.name}</strong>
                          <small>{entry.hosts[0] ?? entry.applicationId}</small>
                        </button>
                      </td>
                      <td><TierBadge entry={entry} /></td>
                      <td className="num">{entry.coverage === 'unobserved' ? '—' : number.format(entry.visits24h)}</td>
                      <td className="num">{entry.coverage === 'unobserved' ? '—' : number.format(entry.visits7d)}</td>
                      <td className="num muted">{entry.coverage === 'unobserved' ? '—' : number.format(entry.views7d)}</td>
                      <td className="num">{entry.coverage === 'unobserved' ? '—' : number.format(entry.visitors7d)}</td>
                      <td className="num muted">{entry.coverage === 'unobserved' ? '—' : number.format(entry.bot7d)}</td>
                      <td>{entry.lastVisitAt ? relativeTime(entry.lastVisitAt) : '—'}</td>
                      <td>{entry.coverage === 'unobserved' ? 'Not observed' : <Sparkline days={entry.daily} />}</td>
                    </tr>
                    {expanded && (
                      <tr className="traffic-detail-row">
                        <td colSpan={9}>
                          <Detail entry={entry} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
          {!rows.length && <p className="traffic-muted">Nothing matches this filter.</p>}
        </div>
      )}
    </section>
  )
}
